/**
 * Settings — org-wide integrations, Porter, and map defaults. Ports
 * mockups/05-settings.html to a live page. FPS Maps is all-access, so these are
 * shared defaults for every user (not per-user preferences), matching the
 * mock-up's "applies to all users" subtitle.
 *
 * Data sources, by section:
 *   • Teamwork Integration — GET /api/teamwork/status (connection + last sync +
 *     synced-client count) and GET /api/clients (chip list). "Sync now" POSTs
 *     /api/teamwork/sync, then re-reads status + clients.
 *   • Porter — GET /api/settings returns porterUsage (current-month query count +
 *     est. cost). It reads as zero today because Porter isn't built yet; we render
 *     "No usage yet" rather than faking numbers. The model select persists to
 *     app_settings via porter_model. The Anthropic API-key row is INFORMATIONAL
 *     ONLY — the key lives in Railway's env (ANTHROPIC_API_KEY), not the DB, and
 *     there is deliberately no UI path to read/replace a deployment secret.
 *   • Map Defaults — GET/PUT /api/settings: default_basemap, group_palette,
 *     tier_1_threshold, tier_2_threshold, distance_units. Real, editable, persisted.
 *   • Data & Exports — Census vintage is a static "2020 Census" badge (no backend).
 *     Auto-refresh-datasets toggle persists to app_settings but is labelled as
 *     taking effect only once the (not-yet-built) weekly refresh job ships.
 *     No Google Drive export-filing — decided 2026-07-04: CSV/PNG exports are
 *     download-only, no Drive integration planned.
 *   • Background Jobs — only "Teamwork client sync" is a real job (status derived
 *     from the Teamwork connection). Dataset auto-refresh and the thumbnail
 *     renderer do not exist as jobs; they show an honest "Not implemented" state.
 *
 * Persistence UX: Map Defaults + the Porter model + the toggle autosave on change
 * (debounced) via PUT /api/settings, mirroring the app's document-autosave feel.
 * A small header status line reflects saved/saving, like the Editor.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Briefcase,
  Sparkles,
  Map as MapIcon,
  Database,
  Clock,
  RefreshCw,
  Image as ImageIcon,
  Check,
  Loader2,
} from 'lucide-react';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import { relativeTime, formatNumber } from '../utils/format';
import { useToast } from '../context/ToastContext';
import './Settings.css';

// Basemap select: value (persisted, matches maps.basemap CHECK domain) → label.
// Kept in lockstep with the Editor topbar's segmented control (Editor.js
// BASEMAP_OPTIONS) so the default set a map is created with always matches a
// choice the editor can highlight. "voyager" is the friendly "Streets" default;
// "streets" is the raw OpenStreetMap style.
const BASEMAP_OPTIONS = [
  { value: 'voyager', label: 'Streets' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'streets', label: 'OSM (OpenStreetMap)' },
];

// Porter model select. Values match .env.example's PORTER_MODEL vocabulary.
const PORTER_MODEL_OPTIONS = [
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 (recommended)' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
];

const DISTANCE_OPTIONS = [
  { value: 'miles', label: 'Miles' },
  { value: 'km', label: 'Kilometers' },
];

// How many client chips to show before collapsing to a "+N more" chip.
const MAX_CLIENT_CHIPS = 8;

export default function Settings() {
  const { showToast } = useToast();

  // Server-loaded state.
  const [settings, setSettings] = useState(null); // Map Defaults + porter_model
  const [porterUsage, setPorterUsage] = useState(null);
  const [teamwork, setTeamwork] = useState(null); // GET /api/teamwork/status shape
  const [clients, setClients] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // 'idle' | 'saving' | 'saved' — mirrors the Editor's autosave status line.
  const [saveState, setSaveState] = useState('idle');

  // Load everything the page needs in parallel on mount.
  const loadAll = useCallback(async () => {
    const [settingsRes, statusRes, clientsRes] = await Promise.allSettled([
      api.get('/settings'),
      api.get('/teamwork/status'),
      api.get('/clients'),
    ]);

    if (settingsRes.status === 'fulfilled') {
      setSettings(settingsRes.value.settings);
      setPorterUsage(settingsRes.value.porterUsage);
    } else {
      // Settings is the core of this page — if it fails, surface the error.
      throw settingsRes.reason;
    }
    // Teamwork/clients are secondary — a failure there degrades one section, it
    // doesn't blank the page. Store null/empty and let those sections show it.
    setTeamwork(statusRes.status === 'fulfilled' ? statusRes.value : { configured: false, error: true });
    setClients(clientsRes.status === 'fulfilled' ? (clientsRes.value.clients || []) : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadAll()
      .then(() => { if (!cancelled) setLoadError(null); })
      .catch((err) => { if (!cancelled) setLoadError(err.message || 'Could not load settings.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadAll]);

  // ─── Persist Map Defaults / Porter model / toggle (debounced autosave) ──────
  // We keep a ref to the latest pending patch so rapid edits collapse into one
  // PUT, and only the newest value wins.
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});

  const flushSave = useCallback(async () => {
    const patch = pendingPatch.current;
    pendingPatch.current = {};
    if (!patch || Object.keys(patch).length === 0) return;
    setSaveState('saving');
    try {
      const { settings: saved } = await api.put('/settings', patch);
      setSettings(saved);
      setSaveState('saved');
    } catch (err) {
      setSaveState('idle');
      showToast(err.message || 'Could not save settings.', { variant: 'error' });
      // Re-sync from the server so the UI reflects the true persisted state
      // after a rejected write (e.g. a validation 400).
      loadAll().catch(() => {});
    }
  }, [showToast, loadAll]);

  // Optimistically update local state, queue the field for the next debounced PUT.
  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...(prev || {}), [key]: value }));
    pendingPatch.current = { ...pendingPatch.current, [key]: value };
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 700);
  }, [flushSave]);

  // Flush any pending save on unmount so a quick edit-then-navigate still persists.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    flushSave();
  }, [flushSave]);

  // ─── Teamwork "Sync now" ────────────────────────────────────────────────────
  const handleSyncNow = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await api.post('/teamwork/sync', {});
      const c = res.clients || {};
      showToast(`Synced ${c.total ?? 0} clients (${c.created ?? 0} new)`);
      // Refresh status + client chips to reflect the new sync time/count.
      const [statusRes, clientsRes] = await Promise.allSettled([
        api.get('/teamwork/status'),
        api.get('/clients'),
      ]);
      if (statusRes.status === 'fulfilled') setTeamwork(statusRes.value);
      if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.clients || []);
    } catch (err) {
      showToast(err.message || 'Teamwork sync failed.', { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  }, [syncing, showToast]);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main">
        <div className="page settings-page">
          <header className="app-page-header" style={{ marginBottom: 'var(--space-5)' }}>
            <div className="settings-header-row">
              <div>
                <h1 className="app-page-header-title">Settings</h1>
                <p className="app-page-header-subtitle">
                  Integrations, Porter, and map defaults — applies to all users
                </p>
              </div>
              <SaveStatus state={saveState} />
            </div>
          </header>

          {loading && <SettingsSkeleton />}

          {!loading && loadError && (
            <div className="callout callout-error" role="alert">
              <div>
                <div className="callout-title">Couldn’t load settings</div>
                {loadError}
              </div>
            </div>
          )}

          {!loading && !loadError && (
            <>
              <TeamworkSection
                teamwork={teamwork}
                clients={clients}
                syncing={syncing}
                onSyncNow={handleSyncNow}
              />
              <PorterSection
                settings={settings}
                usage={porterUsage}
                onChange={updateSetting}
              />
              <MapDefaultsSection settings={settings} onChange={updateSetting} />
              <DataExportsSection settings={settings} onChange={updateSetting} />
              <BackgroundJobsSection teamwork={teamwork} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Header save-status pill (mirrors the Editor's autosave line) ─────────────
function SaveStatus({ state }) {
  if (state === 'saving') {
    return (
      <span className="settings-save-status is-saving">
        <Loader2 size={13} className="spin" /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="settings-save-status is-saved">
        <Check size={13} /> Saved
      </span>
    );
  }
  return <span className="settings-save-status" aria-hidden="true" />;
}

// ─── Section shell ────────────────────────────────────────────────────────────
function Section({ icon: Icon, title, description, children }) {
  return (
    <div className="settings-section">
      <div className="section-head">
        <Icon />
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

// A single label/description + control row.
function SettingRow({ label, description, children }) {
  return (
    <div className="setting-row">
      <div>
        <div className="setting-label">{label}</div>
        {description && <div className="setting-desc">{description}</div>}
      </div>
      <div className="setting-control">{children}</div>
    </div>
  );
}

// ─── Teamwork Integration ─────────────────────────────────────────────────────
function TeamworkSection({ teamwork, clients, syncing, onSyncNow }) {
  const configured = teamwork?.configured;
  const connected = teamwork?.connected;
  const lastSync = teamwork?.lastSync;
  // Prefer the live client-list length for the chip count; fall back to the
  // status endpoint's syncedClients when the list didn't load.
  const clientCount = clients.length || teamwork?.syncedClients || 0;

  const shown = clients.slice(0, MAX_CLIENT_CHIPS);
  const overflow = clientCount - shown.length;

  let connectionBadge;
  if (!configured) {
    connectionBadge = <span className="badge">Not configured</span>;
  } else if (connected) {
    connectionBadge = <span className="badge badge-success"><span className="badge-dot" />Connected</span>;
  } else {
    connectionBadge = <span className="badge badge-error"><span className="badge-dot" />Unreachable</span>;
  }

  return (
    <Section
      icon={Briefcase}
      title="Teamwork Integration"
      description="Clients are synced from Teamwork companies — the same sync the Receipts app uses"
    >
      <SettingRow
        label="Connection"
        description={configured ? 'frontporchsolutions.teamwork.com' : 'Set TEAMWORK_DOMAIN and TEAMWORK_API_KEY to enable'}
      >
        {connectionBadge}
      </SettingRow>

      <SettingRow
        label="Client sync"
        description="Companies sync automatically every hour. Maps and datasets tag against this client list."
      >
        <span className="status-line muted">
          {lastSync ? `Last synced ${relativeTime(lastSync)}` : 'Never synced'}
        </span>
        <button
          className="btn btn-outline btn-sm"
          onClick={onSyncNow}
          disabled={!configured || syncing}
          title={configured ? 'Sync now' : 'Teamwork not configured'}
        >
          <RefreshCw size={12} className={syncing ? 'spin' : undefined} />
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </SettingRow>

      <div className="eyebrow">Synced clients · {clientCount}</div>
      <div className="client-chips">
        {shown.length === 0 && (
          <span className="client-chips-empty">No clients synced yet.</span>
        )}
        {shown.map((c) => (
          <span key={c.id} className="item-chip">{c.name}</span>
        ))}
        {overflow > 0 && <span className="item-chip">+{overflow} more</span>}
      </div>
    </Section>
  );
}

// ─── Porter (AI Assistant) ────────────────────────────────────────────────────
function PorterSection({ settings, usage, onChange }) {
  const model = settings?.porter_model || PORTER_MODEL_OPTIONS[0].value;
  const hasUsage = usage && usage.queries > 0;

  return (
    <Section
      icon={Sparkles}
      title="Porter — AI Assistant"
      description="One organization-level key, stored server-side. Users never handle API keys."
    >
      <SettingRow
        label="Model"
        description="Haiku covers most map queries; Sonnet for heavier analysis"
      >
        <select
          className="select"
          value={model}
          onChange={(e) => onChange('porter_model', e.target.value)}
        >
          {PORTER_MODEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </SettingRow>

      {/*
        Anthropic API key — INFORMATIONAL ONLY. The key lives in Railway's env
        (ANTHROPIC_API_KEY), not app_settings, and there is deliberately no code
        path for the app to read/display/rewrite a deployment secret from a UI.
        No masked value, no "Replace" button — just a status describing where it
        lives. See routes/settings.js header + project-state.md.
      */}
      <SettingRow
        label="Anthropic API key"
        description="Configured server-side — managed via the deployment environment, not this UI"
      >
        <span className="badge badge-info">Configured server-side</span>
      </SettingRow>

      <SettingRow label="Usage this month" description="Across all users">
        {hasUsage ? (
          <span className="status-line">
            {formatNumber(usage.queries)} {usage.queries === 1 ? 'query' : 'queries'} ·{' '}
            <strong className="usage-cost">
              ${usage.costUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </strong>
          </span>
        ) : (
          // porter_usage is empty today (Porter isn't built) — say so honestly.
          <span className="status-line muted">No usage yet</span>
        )}
      </SettingRow>
    </Section>
  );
}

// ─── Map Defaults ─────────────────────────────────────────────────────────────
function MapDefaultsSection({ settings, onChange }) {
  const basemap = settings?.default_basemap || 'voyager';
  const palette = settings?.group_palette || [];
  const tier1 = settings?.tier_1_threshold ?? '';
  const tier2 = settings?.tier_2_threshold ?? '';
  const units = settings?.distance_units || 'miles';

  return (
    <Section
      icon={MapIcon}
      title="Map Defaults"
      description="Starting values for new maps — each map can override"
    >
      <SettingRow label="Default basemap">
        <select
          className="select"
          value={basemap}
          onChange={(e) => onChange('default_basemap', e.target.value)}
        >
          {BASEMAP_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </SettingRow>

      <SettingRow
        label="Group color palette"
        description="The first color is used for a map’s first group, then the next, and so on"
      >
        <PaletteEditor
          palette={palette}
          onReorderFirst={(color) => {
            // Clicking a swatch promotes it to the front of the pick order.
            const next = [color, ...palette.filter((c) => c !== color)];
            onChange('group_palette', next);
          }}
        />
      </SettingRow>

      <SettingRow
        label="Ad tier thresholds"
        description="Population cutoffs Porter uses for Tier 1 / Tier 2 / Tier 3 targeting suggestions"
      >
        <ThresholdInput
          value={tier1}
          title="Tier 1 minimum"
          onCommit={(n) => onChange('tier_1_threshold', n)}
        />
        <ThresholdInput
          value={tier2}
          title="Tier 2 minimum"
          onCommit={(n) => onChange('tier_2_threshold', n)}
        />
      </SettingRow>

      <SettingRow label="Distance units">
        <select
          className="select"
          value={units}
          onChange={(e) => onChange('distance_units', e.target.value)}
        >
          {DISTANCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </SettingRow>
    </Section>
  );
}

// Clickable palette — swatches show the pick order; clicking one promotes it to
// first. Matches the mock-up's row of colored squares with a selected outline on
// the lead color.
function PaletteEditor({ palette, onReorderFirst }) {
  return (
    <div className="palette">
      {palette.map((color, i) => (
        <button
          key={`${color}-${i}`}
          type="button"
          className={`palette-swatch${i === 0 ? ' selected' : ''}`}
          style={{ background: color }}
          title={i === 0 ? `${color} — first group color` : `Use ${color} first`}
          aria-label={i === 0 ? `${color}, first group color` : `Move ${color} to front`}
          onClick={() => onReorderFirst(color)}
        />
      ))}
    </div>
  );
}

// A numeric threshold field with thousands-separator display. Commits on blur or
// Enter (not on every keystroke) so the debounced save fires on a settled value.
function ThresholdInput({ value, title, onCommit }) {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);

  // Display value: the formatted number when not actively editing, else the raw
  // draft the user is typing.
  const display = editing ? draft : (value === '' || value == null ? '' : formatNumber(value));

  const commit = () => {
    setEditing(false);
    const cleaned = String(draft).replace(/[^\d]/g, '');
    if (cleaned === '') return; // empty → leave the prior value untouched
    const n = parseInt(cleaned, 10);
    if (Number.isFinite(n)) onCommit(n);
  };

  return (
    <input
      className="input narrow"
      inputMode="numeric"
      value={display}
      title={title}
      onFocus={() => { setEditing(true); setDraft(value === '' || value == null ? '' : String(value)); }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.currentTarget.blur(); }
        if (e.key === 'Escape') { setEditing(false); e.currentTarget.blur(); }
      }}
    />
  );
}

// ─── Data & Exports ───────────────────────────────────────────────────────────
function DataExportsSection({ settings, onChange }) {
  const autoRefresh = !!settings?.auto_refresh_datasets;

  return (
    <Section
      icon={Database}
      title="Data & Exports"
      description="Boundary and population data comes from the U.S. Census Bureau (TIGERweb)"
    >
      {/* Census vintage — a static badge, no backend. 2030 data appears here when
          the Census publishes it. */}
      <SettingRow
        label="Census vintage"
        description="Boundaries and population figures. 2030 data will appear here when published."
      >
        <span className="badge badge-info">2020 Census</span>
      </SettingRow>

      {/*
        Auto-refresh datasets — the preference persists, but the weekly refresh
        JOB does not exist yet. The description makes clear the toggle is a
        preference for a not-yet-shipped feature, so flipping it doesn't mislead.
      */}
      <SettingRow
        label="Auto-refresh datasets"
        description="Re-run dataset source queries weekly to catch boundary updates. Takes effect once the scheduled refresh ships — not running yet."
      >
        <Toggle
          on={autoRefresh}
          onToggle={() => onChange('auto_refresh_datasets', !autoRefresh)}
          label="Auto-refresh datasets weekly"
        />
      </SettingRow>

    </Section>
  );
}

// Accessible on/off toggle matching the mock-up's pill. Uses role=switch so it
// reads correctly to assistive tech (the mock-up's bare <div> did not).
function Toggle({ on, onToggle, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`toggle${on ? '' : ' off'}`}
      onClick={onToggle}
    />
  );
}

// ─── Background Jobs ──────────────────────────────────────────────────────────
function BackgroundJobsSection({ teamwork }) {
  // Only the Teamwork sync is a real background job in this codebase. Its health
  // is derived from the live connection status: configured + reachable → Healthy.
  const teamworkConfigured = teamwork?.configured;
  const teamworkConnected = teamwork?.connected;

  let teamworkBadge;
  if (!teamworkConfigured) {
    teamworkBadge = <span className="badge">Not configured</span>;
  } else if (teamworkConnected) {
    teamworkBadge = <span className="badge badge-success">Healthy</span>;
  } else {
    teamworkBadge = <span className="badge badge-error">Unreachable</span>;
  }

  return (
    <Section
      icon={Clock}
      title="Background Jobs"
      description="Monitored via Better Stack heartbeats, same as Receipts"
    >
      <div className="setting-row">
        <div className="status-line">
          <Briefcase size={14} className="job-icon" /> Teamwork client sync
        </div>
        <div className="setting-control">
          {teamworkBadge}
          <span className="status-line muted">Every hour</span>
        </div>
      </div>

      {/*
        Dataset auto-refresh and the thumbnail renderer are NOT real jobs in this
        codebase (thumbnails are synchronous client capture-on-save; weekly
        dataset refresh was never built). Show an honest "Not implemented" state
        rather than a fabricated "Healthy" badge.
      */}
      <div className="setting-row">
        <div className="status-line">
          <Database size={14} className="job-icon" /> Dataset auto-refresh
        </div>
        <div className="setting-control">
          <span className="badge">Not implemented</span>
          <span className="status-line muted">Planned · weekly</span>
        </div>
      </div>

      <div className="setting-row">
        <div className="status-line">
          <ImageIcon size={14} className="job-icon" /> Map thumbnail renderer
        </div>
        <div className="setting-control">
          <span className="badge">Not a job</span>
          <span className="status-line muted">Captured on save</span>
        </div>
      </div>
    </Section>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
function SettingsSkeleton() {
  return (
    <div aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="settings-section">
          <div className="section-head">
            <div className="skeleton" style={{ width: 20, height: 20, borderRadius: 5 }} />
            <div style={{ flex: 1 }}>
              {/* Inline widths: the design system only ships .w-60/.w-70/.w-80
                  skeleton-line helpers, so narrower lines set width directly. */}
              <div className="skeleton skeleton-line" style={{ width: '38%' }} />
              <div className="skeleton skeleton-line w-70" style={{ marginTop: 6 }} />
            </div>
          </div>
          <div className="setting-row">
            <div style={{ flex: 1 }}>
              <div className="skeleton skeleton-line" style={{ width: '28%' }} />
            </div>
            <div className="skeleton skeleton-line" style={{ width: 180 }} />
          </div>
          <div className="setting-row">
            <div style={{ flex: 1 }}>
              <div className="skeleton skeleton-line" style={{ width: '28%' }} />
            </div>
            <div className="skeleton skeleton-line" style={{ width: 180 }} />
          </div>
        </div>
      ))}
    </div>
  );
}
